import { TYPE_OF } from '../constants/type-of'
import { isString } from './string'

/**
 * Replace decimal and thousand separators with the one javascript floating
 * numbers requires.
 *
 * @example
 * parseFormattedNumber(intlEn, '1,234.56') === '1234.56'
 * parseFormattedNumber(intlDe, '1.234,56') === '1234.56'
 *
 * @param {Intl} intl - Modux intl helper
 * @param {string|number} value - The value to be converted
 * @returns {string} The converted value as string
 */
function parseFormattedNumber(intl, value) {
    return intl.parseFormattedNumber(value)
}

/**
 * Check if value is a number.
 *
 * @example
 * isNumber('foo') === false
 * isNumber(NaN) === false
 * isNumber(0) === true
 * isNumber(123) === true
 *
 * @private
 * @param {*} [number] - The value to be checked
 * @returns {boolean} Whether the given value is a number or not
 */
function isNumber(number) {
    return !isNaN(number) && (typeof number === TYPE_OF.NUMBER || number instanceof Number)
}

/**
 * Checks if a given value is a valid number and not a zero.
 *
 * @param {*} [number]
 * @returns {boolean}
 */
function isNonZeroNumber(number) {
    return isNumber(number) && Number(number) !== 0
}

/**
 * Check if given value is a valid number which is greater then zero.
 *
 * @example
 * isValidNumber() === false
 * isValidNumber(0) === false
 * isValidNumber(123) === true
 *
 * @param {*} [number] - The value to be checked
 * @returns {boolean} Whether the given value is a valid number or not
 */
function isValidNumber(number) {
    return isNumber(number) && number !== 0
}

/**
 * Check if value (string or number) can be directly converted to an Integer
 *
 * @example
 * isInteger('foo') === false
 * isInteger(NaN) === false
 * isInteger(0.2) === false
 * isInteger('0a') === false
 * isInteger('1.0') === true
 * isInteger('10') === true
 * isInteger(10) === true
 *
 * @param {number|string} [value] - The value to be checked.
 * @returns {boolean} Whether the given value is a direct integer
 */
function isInteger(value) {
    if (!(isNumber(value) || isString(value))) {
        return false
    }
    return Number.isInteger(typeof value === 'number' ? value : Number(value))
}

export { isInteger, isNonZeroNumber, isNumber, isValidNumber, parseFormattedNumber }
